// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// game.ts — the whole ship, headless. No DOM, no wall clock, no Math.random.
//
// THE ONE RULE EVERYTHING ELSE HANGS OFF is the POWERED PREFIX:
//
//     conducts(c) = flood < 100  &&  tick >= c.cutUntil  &&  tick >= tripUntil
//     cost(c)     = reactor ? 0 : destroyed ? 3 : warp ? 2 : charge > 0 ? 0 : 1
//     poweredLen  = the longest k where spine[0..k) all conduct and the costs sum <= output
//
// A compartment does work IF AND ONLY IF it lies in that prefix. tests/mechanism.test.ts re-derives
// exactly this from the raw tick stream, with the constants written out as literals, and counts every
// deviation in BOTH directions at zero tolerance — a system acting while unpowered, and a system that
// was powered and did nothing. That is a count of rule violations, not a duration: a pump draining
// water while dark is wrong on second one and wrong on second three hundred.
//
// TWO SOURCES OF RANDOMNESS, BOTH STATELESS BY DESIGN.
//   * The hazard schedule is generated ONCE from (seed, mode) at construction and lives in the
//     snapshot, so a promoted host inherits the crew's already-spoken forecast instead of destroying
//     it — and so all shared randomness comes from the lobby seed (P2P contract gate #4).
//   * Shorts are hashed from (seed, tick, cell) rather than drawn from a stepped stream, so any peer
//     computes the same answer at any time without carrying rng state across a host transfer.
// Neither ever needs to be serialised, which is the point.

import { MODES, type Mode, cellCount, modeOf, neighbours } from './modes';
import { T } from './tuning';

export type LossCause = 'flood' | 'destroyed' | 'scram' | 'vent' | 'drift';

export interface Cell {
  fire: number;
  flood: number;
  breaches: number;
  /** Capped at 1 — a bulkhead can only be scarred once, so cost tops out at 2 and the graph can
   *  never be priced out of reach. */
  warp: 0 | 1;
  destroyed: boolean;
  /** Seconds a full fire has burned here, toward DESTROY_SECS. */
  burn: number;
  /** Battery unit-seconds, or -1 in a compartment that holds no cell. */
  charge: number;
  drowned: boolean;
  /** Tick until which a short has cut conduction here. */
  cutUntil: number;
}

export interface Hazard {
  t: number;
  cell: number;
  kind: 0 | 1; // 0 breach, 1 fire
}

/** What the mechanism auditor is handed each tick. Raw inputs only — never a derived answer. */
export interface TickRec {
  tick: number;
  output: number;
  tripUntil: number;
  spine: number[];
  /** Per spine cell, in spine order: [index, flood, warp, destroyed, charge, cutUntil, fire]. */
  cells: number[][];
  /** Every compartment's flood/fire, so "was the precondition met" can be checked off-board too. */
  flood: number[];
  fire: number[];
  heat: number;
}

export interface SeatLedger {
  actions: number;
  doused: number;
  sealed: number;
  cellSecs: number;
}

export type Ev =
  | { k: 'pump'; c: number }
  | { k: 'douse'; c: number }
  | { k: 'cool' }
  | { k: 'advance' }
  | { k: 'sense' }
  | { k: 'charge'; seat: number; amount: number; ordinal: number; verb: number }
  | { k: 'bus_trip'; until: number }
  | { k: 'conduction_lost'; c: number; until: number }
  | { k: 'short'; c: number }
  | { k: 'breach'; c: number }
  | { k: 'ignite'; c: number }
  | { k: 'destroy'; c: number }
  | { k: 'seal'; c: number }
  | { k: 'vent'; c: number }
  | { k: 'scram' }
  | { k: 'commit'; len: number }
  | { k: 'over'; cause: LossCause | null; win: 0 | 1 };

/** Client -> host, and the local path too. ABSOLUTE, never a delta, so a duplicate is a no-op. */
export interface Act {
  /** 0 setSpine, 1 seal, 2 douse, 3 vent, 4 forceDown, 5 forceUp */
  k: 0 | 1 | 2 | 3 | 4 | 5;
  c?: number;
  p?: number[];
  /** The client's estimated sim tick at PRESS — the load window is charged on start, not commit. */
  tk: number;
  /** Per-seat monotonic sequence, for idempotency. */
  sq: number;
}

export const VERB_HEAT = (): number[] => [
  T.HEAT_REDRAW_BASE,
  T.HEAT_SEAL,
  T.HEAT_DOUSE,
  T.HEAT_VENT,
  T.HEAT_FORCE_START,
  0,
];

/**
 * A stateless hash in [0,1) from three integers. Used for the short roll so that "did this wet, live
 * compartment short on tick 4711?" has the same answer on every peer, forever, with nothing carried.
 */
export function roll(seed: number, tick: number, cell: number): number {
  let h = (seed ^ 0x9e3779b9) >>> 0;
  h = Math.imul(h ^ (tick + 0x85ebca6b), 0xcc9e2d51) >>> 0;
  h = Math.imul(h ^ (cell + 0xc2b2ae35), 0x1b873593) >>> 0;
  h ^= h >>> 15;
  h = Math.imul(h, 0x85ebca6b) >>> 0;
  h ^= h >>> 13;
  return (h >>> 0) / 4294967296;
}

/**
 * Hazard times and places, from (seed, mode, party) alone. Public, and identical on every peer.
 *
 * THE SHIP FAILS FASTER WITH A BIGGER CREW, and it has to. Measured without this, Hauler's win rate
 * went 87% solo, 82% at two, 72% at four — more hands meant more repairs, more repairs meant more
 * heat, and a fourth player made the run strictly worse. Scaling the damage with the crew is the
 * standard co-op fix and it is the honest one: four people should face four people's worth of ship.
 * `party` comes from the frozen roster, so every peer computes the same schedule.
 */
export function buildSchedule(seed: number, m: Mode, party = 1, horizonSecs = 600): Hazard[] {
  // THE SHIP DOES NOT SCALE WITH THE CREW, and that is a measured decision rather than an omission.
  // Scaling hazards up with party size is the standard co-op fix and here it was actively harmful:
  // at 1/(0.72+0.28p) — 79% more hazards for four hands — win rates went 88/5/0, and even a nearly
  // flat 1/(0.97+0.03p) still left four hands worse off than one.
  //
  // The reason is structural. The load window is a REACTOR budget, not a per-player one: four hands
  // buy roughly 50% more actions than a soloist, not four times as many, because the fourth action
  // inside 2.5 seconds trips the bus no matter whose thumb it was. A crew's real advantage over a
  // soloist is RESPONSE LATENCY — each player watching their own quarter of the ship — which a sim
  // whose hands all see everything instantly cannot measure at all. So the honest thing is to leave
  // the ship the same size and let the extra hands be worth exactly what they are worth.
  //
  // `party` is still taken and still validated, so the schedule stays a pure function of the frozen
  // roster: if this ever does need to scale, it scales identically on every peer.
  const crew = 1;
  void party;
  const n = cellCount(m);
  const out: Hazard[] = [];
  // The cold open: the first hazard lands at 1.5s in a compartment ADJACENT to the reactor but off
  // the cheapest HELM/COOLANT run, so the player's very first drag has a visible consequence inside
  // five seconds instead of watching a calm ship for fifteen.
  const near = neighbours(m, m.reactor).filter((c) => m.kind[c] === 'conduit');
  let t = 1.5;
  let k = 0;
  out.push({ t, cell: near.length ? near[near.length - 1] : (m.reactor + 1) % n, kind: 0 });
  while (t < horizonSecs) {
    const gap = Math.max(m.hazardFloor, m.hazardStart - m.hazardStep * Math.floor(t / 20)) * crew;
    t += gap;
    k++;
    let cell = Math.floor(roll(seed, k * 7919, 31) * n);
    if (cell === m.reactor) cell = (cell + 1) % n;
    out.push({ t, cell, kind: roll(seed, k * 104729, 17) < 1 - m.fireBias ? 0 : 1 });
  }
  return out;
}

export interface GameCfg {
  seed: number;
  mode: Mode | string;
  seats: number;
  /** Balance-sim control arm: strip the battery cells from the ship. Disabled by construction. */
  noCells?: boolean;
  /** The tutorial runs with the director empty and applies its own scripted hazards. */
  noHazards?: boolean;
}

export class Game {
  readonly mode: Mode;
  readonly seed: number;
  readonly n: number;
  cells: Cell[];
  spine: number[];
  poweredLen = 0;
  output = 0;
  tick = 0;
  t = 0;
  heat = 30;
  hull: number;
  transit = 0;
  driftSecs = 0;
  forcing = false;
  scramUntil = 0;
  lastScramT = -999;
  tripUntil = 0;
  win: 0 | 1 = 0;
  over: 0 | 1 = 0;
  cause: LossCause | null = null;
  schedule: Hazard[];
  private nextHazard = 0;
  windowActions: { t: number }[] = [];
  seats: SeatLedger[];
  lastSeq: number[];
  events: Ev[] = [];
  /** Hull removed by each cause, so "what killed the run" is measured, not guessed. */
  hullBy: Record<LossCause, number> = { flood: 0, destroyed: 0, scram: 0, vent: 0, drift: 0 };
  /** Set by the tutorial; consulted by apply() so a wrong move is refused without breaking anything. */
  gate: ((a: Act) => boolean) | null = null;
  /**
   * Fired once per tick at THE moment the powered prefix is settled and before anything reads it.
   * tests/mechanism.test.ts installs this and re-derives the prefix from what it receives — which is
   * only sound because the step below computes the prefix EXACTLY once per tick. Shorts are resolved
   * above this line for that reason: a second recompute after an effect had already been emitted
   * would mean the audit and the game were reasoning about two different ships.
   */
  onTick: ((r: TickRec) => void) | null = null;
  private floodDelta: Float64Array;
  private fireDelta: Float64Array;

  constructor(cfg: GameCfg) {
    this.mode = typeof cfg.mode === 'string' ? modeOf(cfg.mode) : cfg.mode;
    this.seed = cfg.seed | 0;
    this.n = cellCount(this.mode);
    this.hull = this.mode.hull;
    this.seats = Array.from({ length: Math.max(1, cfg.seats) }, () => ({
      actions: 0,
      doused: 0,
      sealed: 0,
      cellSecs: 0,
    }));
    this.lastSeq = this.seats.map(() => -1);
    this.cells = Array.from({ length: this.n }, (_, i) => ({
      fire: 0,
      flood: 0,
      breaches: 0,
      warp: 0,
      destroyed: false,
      burn: 0,
      charge: !cfg.noCells && this.mode.cells.includes(i) ? T.CELL_CAPACITY : -1,
      drowned: false,
      cutUntil: 0,
    }));
    // Cold open: one conduit already taking water, so the ship is failing before the player arrives.
    const wet = this.mode.kind.findIndex((k, i) => k === 'conduit' && i !== this.mode.reactor);
    if (wet >= 0) this.cells[wet].flood = 18;
    this.schedule = cfg.noHazards ? [] : buildSchedule(this.seed, this.mode, this.seats.length);
    this.spine = [this.mode.reactor];
    this.litMask = new Uint8Array(this.n);
    this.floodDelta = new Float64Array(this.n);
    this.fireDelta = new Float64Array(this.n);
    this.recompute();
  }

  // ── the powered prefix ────────────────────────────────────────────────────

  costOf(i: number): number {
    if (i === this.mode.reactor) return 0;
    const c = this.cells[i];
    if (c.destroyed) return T.COST_DESTROYED;
    if (c.warp) return T.COST_WARP;
    return c.charge > 0 ? 0 : 1;
  }

  conducts(i: number): boolean {
    const c = this.cells[i];
    return c.flood < 100 && this.tick >= c.cutUntil && this.tick >= this.tripUntil;
  }

  /** Which compartments are on the powered prefix, as a mask, so `lit()` is not a linear scan. */
  private litMask: Uint8Array;
  /** Index of each lit system, or -1. Filled by recompute so nothing re-scans the spine per tick. */
  private litKind: Record<string, number> = {};

  recompute(): void {
    let out = this.mode.output + (this.forcing ? T.FORCE_OUTPUT : 0);
    if (this.tick < this.scramUntil) out = T.SCRAM_OUTPUT;
    this.output = Math.max(0, out);
    let spent = 0;
    let len = 0;
    for (const i of this.spine) {
      if (!this.conducts(i)) break;
      const c = spent + this.costOf(i);
      if (c > this.output) break;
      spent = c;
      len++;
    }
    this.poweredLen = len;
    this.litMask.fill(0);
    this.litKind = { helm: -1, coolant: -1, pump: -1, suppression: -1, sensors: -1 };
    for (let k = 0; k < len; k++) {
      const i = this.spine[k];
      this.litMask[i] = 1;
      const kind = this.mode.kind[i];
      if (kind !== 'conduit' && kind !== 'reactor' && this.litKind[kind] < 0) this.litKind[kind] = i;
    }
  }

  /** Is this compartment on the powered prefix — i.e. does it work? */
  lit(i: number): boolean {
    return this.litMask[i] === 1;
  }

  /** Index of the lit compartment of this kind, or -1. */
  private kindLit(kind: string): number {
    return this.litKind[kind] ?? -1;
  }

  get segments(): number {
    return Math.max(0, this.poweredLen - 1);
  }

  /** Seconds of forecast the crew's HELM lens can see. */
  get horizon(): number {
    return this.kindLit('sensors') >= 0 ? T.SENSORS_HORIZON : T.BASE_HORIZON;
  }

  // ── crew actions ──────────────────────────────────────────────────────────

  /** The rolling load window's ordinal for an action pressed at sim tick `tk`. */
  ordinalAt(tk: number): number {
    const at = tk / T.TICK_HZ;
    let n = 1;
    for (const a of this.windowActions) if (at - a.t < T.WINDOW_SECS && a.t <= at) n++;
    return n;
  }

  /**
   * Apply a crew action. Returns false if it was refused (illegal, duplicate, or blocked by the
   * tutorial gate) — the caller uses that to play `nope` rather than silently swallowing the tap.
   *
   * ABSOLUTE and IDEMPOTENT: `setSpine` carries the whole route and the verbs name a compartment, so
   * re-delivering a command can never double-charge the core. `duplicateChargeViolations === 0` in
   * the mechanism audit is what proves it, under a 2% command-drop transport with re-sends.
   */
  apply(seat: number, a: Act): boolean {
    if (this.over) return false;
    if (seat < 0 || seat >= this.seats.length) return false;
    if (a.sq <= this.lastSeq[seat]) return false;
    if (this.gate && !this.gate(a)) return false;

    const c = a.c ?? -1;
    const cell = c >= 0 && c < this.n ? this.cells[c] : null;
    let base = 0;
    let changed = 0;

    switch (a.k) {
      case 0: {
        const next = this.sanitiseSpine(a.p ?? []);
        const before = new Set(this.spine);
        const after = new Set(next);
        for (const i of after) if (!before.has(i)) changed++;
        for (const i of before) if (!after.has(i)) changed++;
        if (changed === 0) return false;
        this.spine = next;
        base = T.HEAT_REDRAW_BASE + T.HEAT_REDRAW_PER_CELL * changed;
        this.events.push({ k: 'commit', len: next.length });
        break;
      }
      case 1:
        if (!cell || cell.breaches <= 0) return false;
        cell.breaches--;
        if (this.mode.sealWarps) cell.warp = 1;
        this.seats[seat].sealed++;
        base = T.HEAT_SEAL;
        this.events.push({ k: 'seal', c });
        break;
      case 2:
        if (!cell || cell.fire <= 0) return false;
        cell.fire = Math.max(0, cell.fire - T.DOUSE_RATE);
        if (cell.fire === 0) cell.burn = 0;
        this.seats[seat].doused++;
        base = T.HEAT_DOUSE;
        this.events.push({ k: 'douse', c });
        break;
      case 3:
        if (!cell || cell.fire <= 0) return false;
        cell.fire = 0;
        cell.burn = 0;
        cell.warp = 1;
        this.damage(T.VENT_HULL, 'vent');
        base = T.HEAT_VENT;
        this.events.push({ k: 'vent', c });
        break;
      case 4:
        if (this.forcing) return false;
        this.forcing = true;
        base = T.HEAT_FORCE_START;
        break;
      case 5:
        if (!this.forcing) return false;
        this.forcing = false;
        break;
    }

    this.lastSeq[seat] = a.sq;
    this.seats[seat].actions++;
    if (base > 0) this.charge(seat, base, a.tk, a.k);
    this.recompute();
    return true;
  }

  /**
   * The load window. Every path that can charge the core comes through here — a player command, a
   * redraw, a vent, a FORCE start. Forgetting one makes the game quietly EASIER while every outcome
   * assertion stays green, which is why `unchargedActionViolations` audits both directions.
   */
  private charge(seat: number, base: number, tk: number, verb: number): void {
    const at = tk / T.TICK_HZ;
    const ordinal = this.ordinalAt(tk);
    // The multiplier is SUB-LINEAR in the ordinal (1x, 1.6x, 2.2x, then the trip). At a flat `base x
    // ordinal` a crew of four was strictly worse than a soloist in every mode — it took 30% more
    // actions and lost more runs, because bunching cost it more heat than the extra work was worth.
    // The cliff at the fourth action is what carries the mechanic; the slope up to it only has to
    // make bunching expensive, not ruinous.
    this.heat += base * (1 + (ordinal - 1) * T.ORDINAL_SLOPE);
    this.windowActions.push({ t: at });
    this.events.push({
      k: 'charge',
      seat,
      amount: base * (1 + (ordinal - 1) * T.ORDINAL_SLOPE),
      ordinal,
      verb,
    });
    if (ordinal >= T.TRIP_ORDINAL) this.busTrip();
  }

  private busTrip(): void {
    this.tripUntil = this.tick + T.TRIP_BLACKOUT_TICKS;
    this.heat += T.TRIP_HEAT;
    this.damage(T.TRIP_HULL, 'scram');
    this.windowActions.length = 0;
    this.events.push({ k: 'bus_trip', until: this.tripUntil });
  }

  /** Keep only routes that start at the reactor and step orthogonally, with no repeats. */
  private sanitiseSpine(p: readonly number[]): number[] {
    const out = [this.mode.reactor];
    const seen = new Set(out);
    for (const i of p) {
      if (!Number.isInteger(i) || i < 0 || i >= this.n || seen.has(i)) continue;
      if (!neighbours(this.mode, out[out.length - 1]).includes(i)) break;
      out.push(i);
      seen.add(i);
    }
    return out;
  }

  private damage(amount: number, cause: LossCause): void {
    this.hull -= amount;
    this.hullBy[cause] += amount;
  }

  // ── the sim ───────────────────────────────────────────────────────────────

  hostStep(dt: number): void {
    if (this.over) return;
    this.tick++;
    this.t += dt;
    const m = this.mode;

    // 1. hazards, from the public schedule
    while (this.nextHazard < this.schedule.length && this.schedule[this.nextHazard].t <= this.t) {
      const h = this.schedule[this.nextHazard++];
      this.strike(h.cell, h.kind);
    }

    this.recompute();

    // 2. THE SECOND ANTAGONISM: a wet compartment you are POWERING can short. Pumping the water out
    //    is what sets the room alight, and routing through a wet compartment is a live risk.
    //
    //    Resolved HERE, above the capture point, so the tick has exactly one powered prefix. When
    //    this sat below the effects, a short could truncate the prefix after a pump had already
    //    drained on the old one, and the auditor would have been re-deriving a ship that never
    //    existed on that tick.
    for (let k = 0; k < this.poweredLen; k++) {
      const i = this.spine[k];
      const c = this.cells[i];
      if (c.flood < T.SHORT_FLOOD_AT) continue;
      if (roll(this.seed, this.tick, i) >= T.SHORT_P * dt) continue;
      c.fire = Math.min(100, c.fire + T.SHORT_FIRE);
      c.cutUntil = this.tick + Math.round(T.SHORT_CUT_SECS * T.TICK_HZ);
      this.heat += T.SHORT_HEAT;
      this.damage(T.SHORT_HULL, 'flood');
      this.events.push({ k: 'short', c: i });
      this.events.push({ k: 'conduction_lost', c: i, until: c.cutUntil });
      this.recompute();
      break; // at most one short per tick, so a wet prefix cannot cascade inside a single frame
    }

    // 3. THE CAPTURE POINT. From here to the end of the tick the prefix does not move.
    if (this.onTick) {
      this.onTick({
        tick: this.tick,
        output: this.output,
        tripUntil: this.tripUntil,
        spine: this.spine.slice(),
        cells: this.spine.map((i) => {
          const c = this.cells[i];
          return [i, c.flood, c.warp, c.destroyed ? 1 : 0, c.charge, c.cutUntil, c.fire];
        }),
        flood: this.cells.map((c) => c.flood),
        fire: this.cells.map((c) => c.fire),
        heat: this.heat,
      });
    }

    // 4. battery cells drain on the prefix and trickle back off it
    for (let i = 0; i < this.n; i++) {
      const c = this.cells[i];
      if (c.charge < 0) continue;
      if (c.drowned) {
        c.charge = 0;
        continue;
      }
      if (c.flood >= T.CELL_DROWN_FLOOD) {
        c.drowned = true;
        c.charge = 0;
        continue;
      }
      if (this.lit(i)) {
        c.charge = Math.max(0, c.charge - T.CELL_DRAIN * dt);
        for (const s of this.seats) s.cellSecs += (dt * T.CELL_DRAIN) / this.seats.length;
      } else {
        c.charge = Math.min(T.CELL_CAPACITY, c.charge + T.CELL_RECHARGE * dt);
      }
    }

    // 5. water
    const severity = Math.pow(T.FLOOD_SEVERITY, this.t / 15);
    const pumpAt = this.kindLit('pump');
    const pumpCells = pumpAt >= 0 ? [pumpAt, ...neighbours(m, pumpAt)] : [];
    const floodDelta = this.floodDelta;
    floodDelta.fill(0);
    for (let i = 0; i < this.n; i++) {
      const c = this.cells[i];
      if (c.breaches > 0) floodDelta[i] += T.FLOOD_INFLOW * c.breaches * severity * dt;
      if (c.flood >= 60) {
        for (const nb of neighbours(m, i)) {
          if (this.cells[nb].flood < c.flood) floodDelta[nb] += T.FLOOD_SPREAD * dt;
        }
      }
    }
    for (const i of pumpCells) {
      if (this.cells[i].flood > 0) {
        floodDelta[i] -= T.PUMP_RATE * dt;
        this.events.push({ k: 'pump', c: i });
      }
    }
    for (let i = 0; i < this.n; i++) {
      const c = this.cells[i];
      c.flood = Math.min(100, Math.max(0, c.flood + floodDelta[i]));
      if (c.flood >= 100) this.damage(T.FLOOD_HULL * dt, 'flood');
    }

    // 6. fire
    const suppAt = this.kindLit('suppression');
    const suppCells = suppAt >= 0 ? new Set([suppAt, ...neighbours(m, suppAt)]) : new Set<number>();
    const fireDelta = this.fireDelta;
    fireDelta.fill(0);
    for (let i = 0; i < this.n; i++) {
      const c = this.cells[i];
      if (c.destroyed) continue;
      const powered = this.lit(i);
      if (c.fire > 0) {
        fireDelta[i] += (powered ? T.FIRE_GROW_POWERED : T.FIRE_GROW) * dt;
        // THE FIRST ANTAGONISM: water kills fire. Letting a compartment flood is a legitimate
        // firefighting move — and at 100% flood it stops conducting, which is the cost.
        if (c.flood >= T.SUPPRESS_FLOOD_AT) fireDelta[i] += T.SUPPRESS_BY_WATER * dt;
        if (c.fire >= 50) {
          for (const nb of neighbours(m, i)) {
            if (!this.cells[nb].destroyed) fireDelta[nb] += T.FIRE_SPREAD * dt;
          }
        }
      }
      if (suppCells.has(i) && c.fire > 0) {
        fireDelta[i] -= T.SUPPRESSION_RATE * dt;
        this.events.push({ k: 'douse', c: i });
      }
    }

    // A hot core cooks THE FAR END OF ITS OWN CABLE — one compartment, the last one on the powered
    // prefix, not all of them. Spread across every powered cell this was a wipe: a trace showed ten
    // compartments all catching at once the instant the gauge passed 80, which is not a pressure,
    // it is a cutscene. Concentrated at the end it is legible, survivable, and it teaches the exact
    // move the mode is about — shorten the line.
    if (this.poweredLen > 1 && this.heat >= T.FIRE_IGNITE_HEAT) {
      const tip = this.spine[this.poweredLen - 1];
      const c = this.cells[tip];
      if (!c.destroyed) {
        if (c.fire <= 0) this.events.push({ k: 'ignite', c: tip });
        fireDelta[tip] += ((this.heat - T.FIRE_IGNITE_HEAT) / T.FIRE_IGNITE_DIV) * 3 * dt;
      }
    }
    for (let i = 0; i < this.n; i++) {
      const c = this.cells[i];
      if (c.destroyed) continue;
      const was = c.fire;
      c.fire = Math.min(100, Math.max(0, c.fire + fireDelta[i]));
      if (was <= 0 && c.fire > 0) this.events.push({ k: 'ignite', c: i });
      if (c.fire >= 100) {
        c.burn += dt;
        if (c.burn >= T.DESTROY_SECS) {
          c.destroyed = true;
          c.fire = 0;
          c.burn = 0;
          this.damage(T.DESTROY_HULL, 'destroyed');
          this.events.push({ k: 'destroy', c: i });
        }
      } else {
        c.burn = 0;
      }
    }

    // 7. heat
    const coolant = this.kindLit('coolant') >= 0;
    if (coolant) this.events.push({ k: 'cool' });
    this.heat +=
      (T.HEAT_PER_SEGMENT * this.segments -
        T.COOL_PASSIVE -
        (coolant ? T.COOL_COOLANT : 0) +
        (this.forcing ? T.FORCE_HEAT : 0)) *
      dt;
    this.heat = Math.max(0, this.heat);
    if (this.heat >= T.SCRAM_AT && this.tick >= this.scramUntil) {
      this.heat = 55;
      this.scramUntil = this.tick + Math.round(T.SCRAM_SECS * T.TICK_HZ);
      this.damage(T.SCRAM_HULL, 'scram');
      this.events.push({ k: 'scram' });
      if (this.t - this.lastScramT < T.SCRAM_DOUBLE_WINDOW) this.strike(m.reactor, 0);
      this.lastScramT = this.t;
      // NOT recomputed here: the prefix for this tick was fixed at the capture point, and a SCRAM
      // cutting the output mid-tick would silently un-power systems that had already acted. It takes
      // effect on the next tick, which is also the only version the auditor can check.
    }

    // 8. transit, and the drift clock that makes a softlock structurally impossible
    const helm = this.kindLit('helm') >= 0;
    if (helm && m.transitRate > 0) {
      this.transit = Math.min(100, this.transit + m.transitRate * dt);
      this.driftSecs = 0;
      this.events.push({ k: 'advance' });
    } else if (m.driftClock) {
      this.driftSecs += dt;
      if (this.driftSecs > T.DRIFT_GRACE) {
        const rate = Math.min(
          T.DRIFT_HULL_CAP,
          T.DRIFT_HULL_PER_BLOCK * Math.floor(this.driftSecs / 10),
        );
        this.damage(rate * dt, 'drift');
      }
    }
    if (this.kindLit('sensors') >= 0) this.events.push({ k: 'sense' });

    // 9. terminate
    if (this.transit >= 100 && m.transitRate > 0) this.end(1);
    else if (this.hull <= 0) this.end(0);
  }

  /** Apply a hazard. Also the tutorial's way of injecting a scripted event into the real machine. */
  strike(cell: number, kind: 0 | 1): void {
    const c = this.cells[cell];
    if (!c || c.destroyed) return;
    if (kind === 0) {
      c.breaches = Math.min(3, c.breaches + 1);
      this.events.push({ k: 'breach', c: cell });
    } else {
      if (c.fire <= 0) this.events.push({ k: 'ignite', c: cell });
      c.fire = Math.min(100, c.fire + 25);
    }
  }

  private end(win: 0 | 1): void {
    this.over = 1;
    this.win = win;
    if (!win) {
      let best: LossCause = 'flood';
      for (const k of Object.keys(this.hullBy) as LossCause[]) {
        if (this.hullBy[k] > this.hullBy[best]) best = k;
      }
      this.cause = best;
    }
    this.events.push({ k: 'over', cause: this.cause, win });
  }

  /** The next hazards the HELM lens may draw. Everything else renders a coarse glyph. */
  forecast(): Hazard[] {
    const end = this.t + this.horizon;
    return this.schedule.filter((h) => h.t > this.t && h.t <= end).slice(0, 6);
  }

  /** Promotion. Nothing to reconstruct: the schedule is in the snapshot and shorts are stateless. */
  resumeHostState(): void {
    this.nextHazard = this.schedule.findIndex((h) => h.t > this.t);
    if (this.nextHazard < 0) this.nextHazard = this.schedule.length;
    this.recompute();
  }
}

export const modeList = (): Mode[] => Object.values(MODES);
